/**
 * Social Monitoring Feed Types
 *
 * Aligned with PostForMe upstream (DayMoonDevelopment/post-for-me)
 * https://github.com/DayMoonDevelopment/post-for-me
 *
 * SDK types are imported where they match exactly; frontend-optimised
 * shapes are kept for camelCase transforms and UI-specific fields.
 */

import type {
  SocialPost as SDKSocialPost,
  SocialAccount as SDKSocialAccount,
  PlatformPost as SDKPlatformPost,
  SocialPostResult as SDKSocialPostResult,
  SocialPostListParams as SDKSocialPostListParams,
  SocialPostCreateParams as SDKSocialPostCreateParams,
  SocialPostUpdateParams as SDKSocialPostUpdateParams,
  SocialAccountListParams as SDKSocialAccountListParams,
  SocialAccountFeedListParams as SDKAccountFeedListParams,
  PlatformConfigurationsDto,
} from "post-for-me/resources";

// Re-export SDK request types for the API layer
export type {
  SDKSocialPost,
  SDKSocialAccount,
  SDKPlatformPost,
  SDKSocialPostResult,
  SDKSocialPostListParams,
  SDKSocialPostCreateParams,
  SDKSocialPostUpdateParams,
  SDKSocialAccountListParams,
  SDKAccountFeedListParams,
};

// ============================================
// POST STATUS
// ============================================
// Upstream PostStatus enum (api/src/social-posts/dto/post.dto.ts)
// DB enum: 'draft' | 'scheduled' | 'processing' | 'posted' | 'processed'

/** Post status values — matches upstream PostStatus enum */
export const PostStatus = {
  Draft: "draft",
  Scheduled: "scheduled",
  Processing: "processing",
  Processed: "processed",
} as const;
export type PostStatus = (typeof PostStatus)[keyof typeof PostStatus];

/** Supported social platforms — matches upstream social_provider enum */
export const Platform = {
  X: "x",
  Instagram: "instagram",
  TikTok: "tiktok",
  Facebook: "facebook",
  YouTube: "youtube",
  LinkedIn: "linkedin",
  Pinterest: "pinterest",
  Bluesky: "bluesky",
  Threads: "threads",
} as const;
export type Platform = (typeof Platform)[keyof typeof Platform];

/** Media / content type (frontend classification for filtering/rendering) */
export const MediaType = {
  Image: "image",
  Video: "video",
  Carousel: "carousel",
} as const;
export type MediaType = (typeof MediaType)[keyof typeof MediaType];

/** Social account connection status — matches upstream SocialAccount.status */
export const SocialAccountStatus = {
  Connected: "connected",
  Disconnected: "disconnected",
} as const;
export type SocialAccountStatus = (typeof SocialAccountStatus)[keyof typeof SocialAccountStatus];

// ============================================
// MEDIA
// ============================================

export interface PlatformMedia {
  url: string;
  thumbnailUrl?: string;
  /** Derived from URL extension — upstream does not store type */
  type: MediaType;
  /** Only available when fetched from live platform APIs (media: any[])
   *  — NOT stored in social_posts table. Null if platform doesn't expose it. */
  dimensions?: { width: number; height: number };
  /** Thumbnail frame timestamp for video (ms). Matches upstream thumbnail_timestamp_ms */
  thumbnailTimestamp?: number | null;
  /** User/product tags. Upstream: Media.Tag { platform: 'facebook' | 'instagram', type: 'user' | 'product' } */
  tags?: Array<{
    id: string;
    type: "user" | "product";
    platform: "facebook" | "instagram";
    x?: number;
    y?: number;
  }>;
  /** If true the media will not be processed and instead posted as-is.
   *  Upstream: SocialPostMediaDto.skip_processing */
  skipProcessing?: boolean;
}

/** Derive media type from URL extension for display/filtering */
export function inferMediaType(url: string): MediaType {
  const ext = url.split("?")[0]?.split(".").pop()?.toLowerCase() ?? "";
  if (["mp4", "mov", "webm", "mkv", "avi"].includes(ext)) return MediaType.Video;
  return MediaType.Image;
}

// ============================================
// SOCIAL ACCOUNT
// ============================================

/** Linked social account — matches upstream social_accounts JSON column item */
export interface SocialAccount {
  id: string;
  platform: string;
  username: string | null;
  userId: string;
  profilePhotoUrl: string | null;
  externalId: string | null;
  status: SocialAccountStatus | null;
  /** Raw metadata from upstream — typed as unknown to avoid drift */
  metadata?: unknown;
}

// ============================================
// SCHEDULED POST (upstream: social_posts table)
// ============================================

/** Per-account override config — matches upstream AccountConfigurationDto */
export interface AccountConfiguration {
  socialAccountId: string;
  configuration: Record<string, unknown>;
}

/** Raw platform configs — re-exported from upstream PlatformConfigurationsDto */
export type PlatformConfigurations = PlatformConfigurationsDto;

/** A user's own scheduled/drafted post.
 *  Lives in the social_posts table. Fetched from GET /social-posts.
 *  May also carry feed-specific fields when enriched with social_post_results
 *  or account feed data. */
export interface SocialPost {
  id: string;
  /** Client-supplied identifier (e.g. campaign ID, internal SKU) */
  externalId?: string | null;
  caption: string;
  /** Alias for caption — used by UI components */
  content?: string;
  status: PostStatus;
  scheduledAt?: string | null;
  /** Media items — type is inferred from URL for UI rendering */
  media: PlatformMedia[];
  /** Convenience: platform derived from first linked social account.
   *  Use {@link socialAccounts} for accuracy — a post may be cross-posted to multiple platforms. */
  platform: Platform;
  /** Full linked account metadata (from social_accounts JSON column).
   *  Preserves cross-posting info, usernames, profile photos, etc. */
  socialAccounts: SocialAccount[];
  /** Platform-specific configurations (e.g. Instagram placement, TikTok privacy).
   *  Upstream: platform_configurations */
  platformConfigurations?: PlatformConfigurations;
  /** Per-account override configurations.
   *  Upstream: account_configurations */
  accountConfigurations?: AccountConfiguration[];
  /** Live URL on the platform — populated from social_post_results or account feed */
  platformUrl?: string;
  /** Raw platform metrics — upstream PlatformPostDto.metrics shape.
   *  Only present when enriched from /social-account-feeds with expand=metrics */
  rawMetrics?: unknown;
  /** Account feed linkage fields */
  socialAccountId?: string;
  socialPostResultId?: string;
  socialPostId?: string;
  platformAccountId?: string;
  externalAccountId?: string;
  platformData?: unknown;
  postedAt?: string;
  createdAt: string;
  updatedAt?: string | null;
}

// ============================================
// PLATFORM POST (upstream: PlatformPostDto from /social-account-feeds)
// ============================================
// This is a LIVE platform post — already published. Not a scheduled post.
// It comes from the platform's API (Instagram Graph API, TikTok API, etc).

/** A post fetched from a platform's live API via /social-account-feeds/:id.
 *  These are already published — NOT scheduled/drafted posts. */
export interface PlatformPost {
  /** Our internal social_post ID (if linked) */
  socialPostId?: string;
  /** Our internal social_post_result ID (if linked) */
  socialPostResultId?: string;
  /** Platform post ID (e.g. Instagram media ID, TikTok video ID) */
  platformPostId: string;
  /** External post ID from the platform */
  externalPostId?: string | null;
  /** The social account this post belongs to */
  socialAccountId: string;
  /** Platform's account ID */
  platformAccountId: string;
  /** Platform's external account ID */
  externalAccountId?: string | null;
  /** Platform name */
  platform: Platform;
  /** Caption or text content */
  caption: string;
  /** Media items */
  media: PlatformMedia[];
  /** URL to the post on the platform */
  platformUrl: string;
  /** When the post was published */
  postedAt?: string;
  /** Platform-specific raw metrics — NEVER normalize.
   *  Shape depends entirely on the platform:
   *    • Instagram → { like_count, comments_count, shares_count, saves_count }
   *    • TikTok    → { play_count, share_count, comment_count, digg_count }
   *    • YouTube   → { viewCount, likeCount, commentCount }
   *    • etc.
   *  Only present when fetched with expand=['metrics'] */
  metrics?: unknown;
  /** Platform-specific extra data (e.g. YouTube platform_data) */
  platformData?: unknown;
  /** Created / fetched timestamp */
  createdAt: string;
}

// ============================================
// POST RESULT (matches upstream social_post_results table)
// ============================================

export interface SocialPostResult {
  id: string;
  postId: string;
  /** Upstream: social_account_id — the account this result targets */
  socialAccountId: string;
  success: boolean;
  /** Upstream: error — typed as unknown in SDK */
  error: unknown;
  /** Raw platform response payload (platform-specific shape) */
  details: unknown;
  /** Platform-specific post ID and URL — matches SDK SocialPostResult.PlatformData */
  platformData:
    | {
        id?: string | null;
        url?: string | null;
      }
    | null
    | undefined;
  /** Media items returned by the platform (e.g. platform-processed thumbnails) */
  media?: PlatformMedia[] | null;
  createdAt: string;
}

// ============================================
// PAGINATION
// ============================================

/** Offset-based pagination (used by /social-posts) */
export interface PaginatedResponse<T> {
  transformedData: T[];
  count: number;
}

/** Cursor-based pagination metadata (used by /social-account-feeds) */
export interface AccountFeedMeta {
  /** Cursor for fetching the next page */
  cursor: string;
  /** Limit used for this request */
  limit: number;
  /** Full URL to fetch the next page, or null if no more pages */
  next: string | null;
  /** Whether more results are available */
  hasMore: boolean;
}

/** Cursor-paginated response (used by /social-account-feeds) */
export interface AccountFeedResponse<T> {
  /** Posts returned by the platform feed */
  data: T[];
  /** Pagination metadata */
  meta: AccountFeedMeta;
}

// ============================================
// ACCOUNT FEED QUERY
// ============================================

export interface AccountFeedQuery {
  /** Pagination limit (default 50, max 100 per upstream) */
  limit?: number;
  /** Cursor identifying next page of results */
  cursor?: string;
  /** Filter by PostForMe social post ID(s). Multiple = OR logic. */
  socialPostIds?: string[];
  /** Filter by external post ID(s). Multiple = OR logic. */
  externalPostIds?: string[];
  /** Filter by platform post ID(s). Multiple = OR logic. */
  platformPostIds?: string[];
  /** Expand additional data. Use `['metrics']` to include analytics.
   *  ⚠️ Metrics are OPT-IN — omitted by default to reduce payload size. */
  expand?: string[];
}

// ============================================
// FEED FILTERS (for /social-posts scheduled posts)
// ============================================
// All optional fields are omitted from the query when undefined.
//
// ⚠️ UPSTREAM NOTE: SocialPostQueryDto accepts arrays for platform,
//    status, external_id, social_account_id (OR logic for multiple values).
//    Our UI currently uses single-select; expand to arrays when needed.

export interface FeedFilters {
  /** Defaults to Processed on first load */
  status: PostStatus;
  /** Undefined = all platforms */
  platform?: Platform;
  /** Undefined = all media types (client-side only) */
  mediaType?: MediaType;
  /** Undefined = no external_id filter */
  externalId?: string;
  /** Undefined = no social_account_id filter */
  socialAccountId?: string;
  /** Pagination limit (default 20, max 100 per upstream) */
  limit?: number;
  /** Pagination offset (default 0) */
  offset?: number;
}

// Alias for API layer
export type SocialPostsQuery = FeedFilters;

// ============================================
// WEBHOOKS (not yet in SDK v2.7.0 — manual types)
// ============================================

export const WebhookEvent = {
  SocialPostCreated: "social.post.created",
  SocialPostUpdated: "social.post.updated",
  SocialPostDeleted: "social.post.deleted",
  SocialPostResultCreated: "social.post.result.created",
  SocialAccountCreated: "social.account.created",
  SocialAccountUpdated: "social.account.updated",
} as const;
export type WebhookEvent = (typeof WebhookEvent)[keyof typeof WebhookEvent];

export interface Webhook {
  id: string;
  url: string;
  eventTypes: WebhookEvent[];
  secret: string;
}

export interface CreateWebhookInput {
  url: string;
  eventTypes: WebhookEvent[];
}

export interface UpdateWebhookInput {
  url?: string;
  eventTypes?: WebhookEvent[];
}

// ============================================
// METRICS HELPERS (auto-generated from SDK)
// ============================================
// Run: node scripts/generate-metric-keys.cjs
// Source of truth: post-for-me SDK type definitions

export {
  METRIC_KEYS,
  readMetric,
  extractMetrics,
  type MetricCategory,
} from "./metric-keys.generated";
